/**
 * Deterministic speaker color palette.
 *
 * 12 hex colors at ~30° hue intervals around the color wheel, tuned so every
 * color is visually distinct in both dark and light modes.
 *
 * Used with Mantine components via the `color` prop (hex accepted in v8).
 * - Badge/Button variant="light" → renders the hex as a tinted background
 * - Text `c` prop → renders directly as text color
 *
 * Uses a djb2-style hash over the full name so speakers with the same first
 * letter still get different colors.
 */

// Colors are the same 12 hues but ordered by stepping +150° each time.
// Adjacent indices are always ~150° apart on the wheel, so even if the hash
// places two speakers at neighbouring indices they will look very different.
//
// Sequence: 0° → 150° → 300° → 90° → 240° → 30° → 180° → 330° → 120° → 270° → 60° → 210°
export const SPEAKER_COLORS = [
  "#ff6b6b", // red       0°
  "#18a890", // emerald   150°
  "#c030a8", // fuchsia   300°
  "#7ab820", // lime      90°
  "#6040d8", // indigo    240°
  "#e07a38", // orange    30°
  "#2090c8", // sky       180°
  "#d83070", // rose      330°
  "#28a855", // green     120°
  "#9030d8", // violet    270°
  "#c49a10", // amber     60°
  "#3060d8", // blue      210°
] as const

export type TSpeakerColor = (typeof SPEAKER_COLORS)[number]

/**
 * Identity hue for an unnamed diarisation slot. Slot 0, 1, 2… walk the palette
 * so two people who still need a name never share a colour. Negative slots wrap
 * the same way. Graphics and avatar fills only — text uses {@link identityTextColor}.
 */
export function unnamedSlotColor(slot: number): TSpeakerColor {
  const len = SPEAKER_COLORS.length
  const idx = ((slot % len) + len) % len
  return SPEAKER_COLORS[idx]
}

/**
 * Assign colors by position in the ordered speaker list.
 * First speaker → index 0, second → index 1, etc.
 * Guarantees zero collisions for up to 12 speakers.
 *
 * When `profileColors` is provided, speakers with a saved color override get
 * that color instead of the palette index; the override is accepted as-is
 * (it's a user-chosen hex value, not necessarily in the palette).
 */
export function buildSpeakerColorMap(names: string[], profileColors?: Map<string, string | null>): Map<string, string> {
  // Both the build keys and the lookups normalise via `normalizeSpeakerKey`
  // so the same person — "Mark", "Mark Smith", "Mark (Mark Smith)" — resolves
  // to one colour regardless of which decorated form the downstream render
  // path happens to pass.
  //
  // Values are typed `string`, not `TSpeakerColor`: an override is a user-chosen
  // hex that need not be one of the 12 palette literals, so there is nothing to
  // cast — the palette fallback happens to be a `TSpeakerColor`, but the map's
  // contract is "some CSS colour string".
  const map = new Map<string, string>()
  names.forEach((name, i) => {
    const key = normalizeSpeakerKey(name)
    const override = profileColors?.get(name) ?? profileColors?.get(key)
    map.set(key, override ?? SPEAKER_COLORS[i % SPEAKER_COLORS.length])
  })
  return map
}

/**
 * Strip downstream display decorations from a speaker name before hashing.
 *
 * The same person can show up as `"Mark"` (action-owner row), `"Mark Smith"`
 * (attendee card with full name), or `"Mark (Mark Smith)"` (attendee card
 * rendering both display name and original diarisation label). All three
 * should hash to one identity colour. Normalisation:
 *   - drop everything after `(`
 *   - collapse whitespace
 *   - lowercase
 *   - trim
 *
 * Exported so the context-map lookup can normalise keys the same way.
 */
export function normalizeSpeakerKey(name: string): string {
  return name.split("(")[0].trim().toLowerCase().replace(/\s+/g, " ")
}

/**
 * Colour map for an ordered meeting roster — the dashboard card and the
 * compact list row. Slot 0 gets palette index 0, slot 1 index 1, the same
 * assignment the meeting page uses (`unnamedSlotColor(slot)`) when nobody has
 * picked a colour. The list payload carries `speaker_names` in that slot order
 * and no saved overrides, so hashing the name would paint two people the same
 * hue (or a different hue than the meeting page).
 *
 * First name wins when two entries normalise to one person, matching the
 * meeting-page map. Does not change `speakerColor` or `buildSpeakerColorMap`.
 */
export function rosterSpeakerColorMap(names: readonly string[]): Map<string, string> {
  const map = new Map<string, string>()
  names.forEach((name, index) => {
    const key = normalizeSpeakerKey(name)
    if (!key || map.has(key)) return
    map.set(key, unnamedSlotColor(index))
  })
  return map
}

/** Fallback hash — used when no canonical colour map is available. */
export function speakerColor(name: string): TSpeakerColor {
  if (!name) return SPEAKER_COLORS[0]
  const key = normalizeSpeakerKey(name)
  if (!key) return SPEAKER_COLORS[0]
  let hash = 5381
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) + hash) ^ key.charCodeAt(i)
  }
  return SPEAKER_COLORS[Math.abs(hash) % SPEAKER_COLORS.length]
}

/**
 * The single colour-resolution chain (audit 3.13). Every consumer — the
 * `SpeakerColorsProvider` context hook, the `MeetingSummary` cards, the Player,
 * and TalkShare — resolves a speaker's colour through this: the canonical
 * `buildSpeakerColorMap` map (by normalised key, then raw name), then the
 * deterministic hash fallback. No positional `indexOf` against a divergent
 * array, so the same person gets the same hue in the legend, the talk-share
 * bar, and the summary cards.
 */
export function resolveSpeakerColor(name: string, colorMap?: Map<string, string>): string {
  const fromMap = colorMap?.get(normalizeSpeakerKey(name)) ?? colorMap?.get(name)
  return fromMap ?? speakerColor(name)
}

// Ink candidates for initials/text sitting ON a solid identity fill. Both
// resolve near-black / white in every theme+scheme (the dark palette inverts),
// so the choice depends only on the fill's own luminance, never the scheme.
const INK_DARK = "var(--mantine-color-dark-9)"
const INK_LIGHT = "var(--mantine-color-white)"

/** WCAG relative luminance (0–1) of a `#rgb` / `#rrggbb` sRGB hex; null if unparseable. */
function relativeLuminance(hex: string): number | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const digits = match[1]
  const full = digits.length === 3 ? digits[0] + digits[0] + digits[1] + digits[1] + digits[2] + digits[2] : digits
  const channel = (offset: number): number => {
    const v = Number.parseInt(full.slice(offset, offset + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
}

/**
 * Ink for initials/text placed ON a speaker's identity fill. Picks dark or
 * white ink by whichever wins WCAG contrast against the fill — the decision
 * `autoContrast` makes, but applied to this palette's arbitrary hexes (Mantine
 * Avatar autoContrast does not fire for non-theme colours). The black/white
 * crossover sits at luminance ≈ 0.18: lighter hues (lime, amber, emerald,
 * orange, sky, green, red) take dark ink; darker hues (indigo, violet, fuchsia,
 * blue, rose) take white.
 */
export function speakerInkColor(name: string, colorMap?: Map<string, string>): string {
  return identityInkColor(resolveSpeakerColor(name, colorMap))
}

/**
 * A speaker's identity colour made legible as TEXT: mixed 55% toward the
 * scheme-correct `--mantine-color-text` anchor so its lightness lands on the
 * readable side of the body in both colour schemes (raw identity hexes fail
 * WCAG on the pastel surfaces). Use for owner / speaker NAME text; graphics
 * (waveform bars, legend dots, band strips) keep the raw hue.
 */
export function speakerTextColor(name: string, colorMap?: Map<string, string>): string {
  return identityTextColor(resolveSpeakerColor(name, colorMap))
}

/**
 * Ink for text/initials ON an already-resolved identity hex (when the caller
 * computed the hue itself, e.g. a diarization-label index). Same crossover as
 * `speakerInkColor`.
 */
export function identityInkColor(color: string): string {
  const lum = relativeLuminance(color)
  if (lum === null) return INK_LIGHT
  return lum > 0.18 ? INK_DARK : INK_LIGHT
}

/** Make an already-resolved identity hex legible as TEXT (mix 55% toward the text anchor). */
export function identityTextColor(color: string): string {
  return `color-mix(in oklab, ${color} 55%, var(--mantine-color-text))`
}
